<template>
  <div v-if="isLoading" class="spin">
    <a-spin />
  </div>
  <ThemeProvider
    v-else
    :theme="{
      rtl,
      topMenu,
      darkMode,
      mainContent,
      ...themeColor,
    }"
  >
    <Suspense>
      <template #default>
        <router-view></router-view>
      </template>
      <template #fallback>
        <div class="spin">
          <a-spin />
        </div>
      </template>
    </Suspense>
  </ThemeProvider>
</template>

<script>
import { ThemeProvider } from "vue3-styled-components";
import { themeColor } from "./config/theme/themeVariables";
import { computed, defineComponent, onMounted, onUnmounted, watch } from "vue";
import { useStore } from "vuex";
import { inject } from "vue";
import request from "./services/request";

import "v-calendar/dist/style.css";

export default defineComponent({
  name: "App",
  components: {
    ThemeProvider,
  },
  setup() {
    const { state, commit } = useStore();
    const currentUser = computed(() => state.currentUser);
    const socket = inject("socket");

    const rtl = computed(() => state.themeLayout.rtlData);
    const isLoading = computed(() => state.themeLayout.loading);
    const darkMode = computed(() => state.themeLayout.data);
    const topMenu = computed(() => state.themeLayout.topMenu);
    const mainContent = computed(() => state.themeLayout.main);

    const attachSocketEvents = () => {
      if (socket && currentUser.value?.id) {
        socket.on("notifications:new", () => {
          fetchNotificationsCounts();
        });
      }
    };

    const detachSocketEvents = () => {
      if (socket && socket.off) {
        socket.off("notifications:new");
      }
    };

    const fetchNotificationsCounts = async () => {
      try {
        const [notificationsRespponse, unResolvedNotesRespponse] =
          await Promise.all([
            request.get("notifications/count"),
            request.get("notes/count"),
          ]);

        commit("setUnResolvedNotesCount", unResolvedNotesRespponse?.data);
        commit("setNotificationsCount", notificationsRespponse?.data);
      } catch (error) {
        console.log(error);
      }
    };

    onMounted(() => {
      // RTL & Dark mode setup
      window.addEventListener("load", () => {
        const domHtml = document.getElementsByTagName("html")[0];
        rtl.value
          ? domHtml.setAttribute("dir", "rtl")
          : domHtml.setAttribute("dir", "ltr");
        darkMode.value ? document.body.classList.add("dark-mode") : "";
      });

      // Check if user is already logged in via localStorage
      const savedUser = localStorage.getItem("user_data");
      if (savedUser && !currentUser.value?.id) {
        try {
          const user = JSON.parse(savedUser);
          commit("setCurrentUser", user); //  Assuming you have a mutation named setCurrentUser
        } catch (e) {
          console.warn("Invalid user_data in localStorage");
        }
      }

      // Attach socket and fetch if logged in
      if (currentUser.value?.id) {
        attachSocketEvents();
        fetchNotificationsCounts();
      }

      // Reactively watch for login changes
      watch(
        () => currentUser.value,
        (newUser) => {
          detachSocketEvents();
          if (newUser?.id) {
            attachSocketEvents();
            fetchNotificationsCounts();
          }
        }
      );
    });

    onUnmounted(() => {
      detachSocketEvents();
    });

    return {
      themeColor,
      rtl,
      darkMode,
      topMenu,
      isLoading,
      mainContent,
    };
  },
});
</script>

<style>
.breadcrumb-styles {
  margin-inline-start: 32px !important;
  margin-top: 32px !important;
}
</style>
